import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import _isEmpty from 'lodash/isEmpty';
import PropTypes from "prop-types";
import React from "react";
import Scan from "src/components/scan/scan";
import UndockConfirmation from "src/components/UndockConfirmation";
import { MHS_MODULES_MAP, MLINE_ACTIONS_ALLOWED_RESOURCES, MODULE_TYPE_TEXT_COLORS } from "src/constants/m-line";
import DockBtn from "src/pages/mline/sections/DockIcon";
import MhsModule from "src/pages/mline/sections/mhs/modules/mhs-module";
import ModuleTransactionsPanel from 'src/pages/mline/sections/transactions/module-transaction-panel';
import UndockIcon from "src/pages/mline/sections/UndockIcon";
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { MLINE_ACTIONS } from "src/utils/constants";

const SingleMHSModuleContainer = ({
  module,
  slot,
  handleConnectModule,
  handleUndockModule,
  skipActions,
  skipTransactions,
  inputModuleTransactions,
  customStatusText,
  children,
  handleRefetchCyclone,
}) => {
  const { openActionPanel }  = useActionPanelStore();

  if (!module) return null;

  const isModuleEmpty = !module;
  const inputModuleParentTransactionUri = inputModuleTransactions[0]?.parent_transaction;
  const hideTransactionsButton = _isEmpty(inputModuleTransactions) || skipTransactions;

  const wrapperClassName = classNames("module-card", {
    [`module-card-bg-${module?.type}`]: !!module,
    "module-card-empty-bg": !module,
  });

  const handleUndock = (moduleSlot) => {
    openActionPanel({
      panelId: 'undock-confirmation',
      title: 'Undock Confirmation',
      content: <UndockConfirmation
        module={module}
        handleAction={() => handleUndockModule(MLINE_ACTIONS.UNDOCK_MODULE, module, moduleSlot)} />,
    });
  };

  const handleDockModule = (moduleSlot) => {
    const moduleName = MHS_MODULES_MAP[moduleSlot];
    const allowedResourcesToScan = MLINE_ACTIONS_ALLOWED_RESOURCES[moduleSlot];

    openActionPanel({
      panelId: 'scan-module-container',
      title: `Scan ${moduleName}`,
      isSidebar: true,
      content: (<Scan
        action={MLINE_ACTIONS.DOCK_MODULE}
        allowedResourcesToScan={allowedResourcesToScan}
        handleAction={(moduleUri) => handleConnectModule(MLINE_ACTIONS.DOCK_MODULE, moduleUri, moduleSlot)} />
      ),
    });
  };

  const handleOpenTransactions = () => {
    openActionPanel({
      panelId: 'transactions-for-module',
      title: "View Transactions",
      customPanelStyle: { minHeight: '45vh' },
      stack: true,
      content: (
        <ModuleTransactionsPanel
          inputModuleParentTransactionUri={inputModuleParentTransactionUri}
          module={module}
          handleRefetchCyclone={handleRefetchCyclone}
        />
      ),
    });
  };

  const renderActionButtons = () => {
    if (skipActions) return null;

    return !isModuleEmpty ? (
      <UndockIcon onUndock={() => handleUndock(module, slot)} />
    ) : (
      <DockBtn onDock={() => handleDockModule(slot)} />
    )
  }

  const renderModuleStatus = () => {
    if (customStatusText) {
      return customStatusText;
    }

    return <span>{isModuleEmpty ? 'Empty' : 'Docked'}</span>
  }

  return (
    <div className="mb25">
      <div className="mhs-modules-container">
        <div key={slot} className={wrapperClassName}>
          <div className="d-flex justify-content-between">
            <div className="d-flex align-items-base">
              <div>
                <FontAwesomeIcon icon={faCoins} className="spacer-right module-title-size" />
              </div>
              <div className="text-left">
                <div className="module-title-size">{MHS_MODULES_MAP[slot]}</div>
                <div className="module-id">
                  {renderModuleStatus()}
                </div>
              </div>
            </div>
            <div>
              {renderActionButtons()}
            </div>
          </div>
          {!isModuleEmpty && (
            <MhsModule module={module}>
              {children}
            </MhsModule>
          )}
        </div>
      </div>
      {!hideTransactionsButton && (
        <button
          type='button'
          className={`btn btn-link view-transactions-btn ${MODULE_TYPE_TEXT_COLORS[module?.type]}`}
          onClick={handleOpenTransactions}
        >
          <FontAwesomeIcon icon={faRepeat} className="spacer-right" />
          View Transactions
        </button>
      )}
    </div>

  );
};

SingleMHSModuleContainer.defaultProps = {
  handleConnectModule: () => {},
  handleUndockModule: () => {},
  skipActions: false,
  customStatusText: '',
  children: null,
  skipTransactions: false,
  inputModuleTransactions: [],
}

SingleMHSModuleContainer.propTypes = {
  /* eslint-disable camelcase */
  module: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    current_batch: PropTypes.string,
  }).isRequired,
  handleConnectModule: PropTypes.func,
  handleUndockModule: PropTypes.func,
  skipActions: PropTypes.bool,
  customStatusText: PropTypes.string,
  children: PropTypes.node,
  slot: PropTypes.string.isRequired,
  skipTransactions: PropTypes.bool,
  inputModuleTransactions: PropTypes.arrayOf(PropTypes.shape({
    parent_transaction: PropTypes.string,
  })),
  handleRefetchCyclone: PropTypes.func.isRequired,
};

export default SingleMHSModuleContainer;
